import React from 'react';
import { Phone, Globe, Mail, AlertTriangle, Heart } from 'lucide-react';

const KeepingYouSafePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Emergency Banner */}
      <div className="bg-red-600 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <AlertTriangle className="w-6 h-6" />
            <span className="font-semibold">
              If you are in immediate danger, call 999 (UK) or your local emergency services
            </span>
            <AlertTriangle className="w-6 h-6" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Heart className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Keeping You Safe
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Your safety and wellbeing are our highest priority. If you're experiencing a crisis, 
            please reach out for immediate support.
          </p>
        </div>

        {/* Crisis Assessment Section */}
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-red-800 mb-6 text-center">
            <strong>Are you feeling suicidal, harming yourself (e.g. pulling hair, cutting, etc) or being bullied, 
            in domestic violence, or at risk to yourself or to another person?</strong>
          </h2>
        </div>

        {/* TwinBrain Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">
            TwinBrain
          </h2>
          <h3 className="text-xl font-semibold text-gray-700">
            *Clinical Psychology & Emotional Well-being
          </h3>
        </div>

        {/* Instructions */}
        <div className="mb-8">
          <div className="bg-gray-50 border-l-4 border-gray-400 p-6 mb-6">
            <p className="text-lg text-gray-800">
              <strong>If Yes,</strong> contact and receive support from a Helpline you know, or from one of the organizations below.
            </p>
          </div>
        </div>

        {/* UK Residents Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            🇬🇧 (a) UK Residents:
          </h2>
          
          <div className="space-y-6">
            {/* Samaritans */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Samaritans</h3>
                  <a href="tel:08457909090" className="text-lg font-mono text-blue-600 hover:underline">
                    08457 909090
                  </a>
                  <p className="text-gray-700 mt-1">
                    24-hour crisis line for people contemplating harming themselves
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Website: <a href="http://www.samaritans.org/" target="_blank" rel="noopener noreferrer" 
                    className="text-blue-600 hover:underline">http://www.samaritans.org/</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Saneline */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Saneline</h3>
                  <div className="space-y-1">
                    <a href="tel:03003047000" className="text-lg font-mono text-blue-600 hover:underline block">
                      0300 304 7000
                    </a>
                    <a href="tel:07984967708" className="text-lg font-mono text-blue-600 hover:underline block">
                      07984 967 708
                    </a>
                  </div>
                  <p className="text-gray-700 mt-2">
                    Emotional support, information and guidance for people affected by mental illness
                  </p>
                </div>
              </div>
            </div>

            {/* Your GP */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Your GP</h3>
              <p className="text-gray-700">
                If you need to, contact your GP on their phone number during your surgery working hours.
              </p>
            </div>

            {/* Single Point of Access */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Single Point of Access (SPA) Crisis Helpline</h3>
              <p className="text-gray-700">
                Google the Single Point of Access Crisis Helpline in your Borough of residence and ask for help 24 hours/seven days.
              </p>
            </div>

            {/* Childline */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Childline</h3>
                  <a href="tel:08454647" className="text-lg font-mono text-blue-600 hover:underline">
                    0845 4647
                  </a>
                  <p className="text-gray-700 mt-1">24 hours a day</p>
                </div>
              </div>
            </div>

            {/* NHS 111 */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">NHS 111</h3>
                  <a href="tel:111" className="text-lg font-mono text-blue-600 hover:underline">
                    111
                  </a>
                  <p className="text-gray-700 mt-1">Free from landlines and mobiles</p>
                </div>
              </div>
            </div>

            {/* NHS Direct Wales */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">NHS Direct Wales</h3>
                  <a href="tel:08454647" className="text-lg font-mono text-blue-600 hover:underline">
                    0845 4647
                  </a>
                  <p className="text-gray-700 mt-1">24 hours a day</p>
                </div>
              </div>
            </div>

            {/* Mind */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Mind</h3>
                  <a href="tel:03001233393" className="text-lg font-mono text-blue-600 hover:underline">
                    0300 123 3393
                  </a>
                  <p className="text-gray-700 mt-1">
                    Mon-Fri: 9am to 6pm (except for bank holidays)
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Website: <a href="http://www.mind.org.uk/" target="_blank" rel="noopener noreferrer" 
                    className="text-blue-600 hover:underline">http://www.mind.org.uk/</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Shout */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Mail className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Shout</h3>
                  <p className="text-lg font-mono text-blue-600">
                    Text SHOUT to 85258
                  </p>
                  <p className="text-gray-700 mt-1">
                    Free 24/7 text support service for anyone in crisis
                  </p>
                </div>
              </div>
            </div>

            {/* National Suicide Prevention Helpline UK */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">National Suicide Prevention Helpline UK</h3>
                  <a href="tel:08006895652" className="text-lg font-mono text-blue-600 hover:underline">
                    0800 689 5652
                  </a>
                  <p className="text-gray-700 mt-1">
                    6pm to 3:30am every day of the year
                  </p>
                </div>
              </div>
            </div>

            {/* Campaign Against Living Miserably (CALM) */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Campaign Against Living Miserably (CALM)</h3>
                  <a href="tel:08006585858" className="text-lg font-mono text-blue-600 hover:underline">
                    0800 658 5858
                  </a>
                  <p className="text-gray-700 mt-1">
                    5pm to midnight, 365 days a year
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Website: <a href="https://www.thecalmzone.net/" target="_blank" rel="noopener noreferrer" 
                    className="text-blue-600 hover:underline">https://www.thecalmzone.net/</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Papyrus */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Papyrus</h3>
                  <a href="tel:08000684141" className="text-lg font-mono text-blue-600 hover:underline">
                    0800 068 4141
                  </a>
                  <p className="text-gray-700 mt-1">
                    Mon-Fri: 10am to 10pm, weekends: 2pm to 10pm, bank holidays: 2pm to 5pm
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Website: <a href="https://www.papyrus-uk.org/" target="_blank" rel="noopener noreferrer" 
                    className="text-blue-600 hover:underline">https://www.papyrus-uk.org/</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* International Residents Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            🌍 (b) International Residents:
          </h2>
          
          <div className="space-y-6">
            {/* International Association for Suicide Prevention */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Globe className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    International Association for Suicide Prevention
                  </h3>
                  <p className="text-gray-700 mb-2">
                    Crisis centers by country
                  </p>
                  <p className="text-sm text-gray-600">
                    Website: <a href="https://www.iasp.info/resources/Crisis_Centres/" target="_blank" rel="noopener noreferrer" 
                    className="text-blue-600 hover:underline">https://www.iasp.info/resources/Crisis_Centres/</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Befrienders Worldwide */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Globe className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Befrienders Worldwide</h3>
                  <p className="text-gray-700 mb-2">
                    Emotional support to prevent suicide worldwide
                  </p>
                  <p className="text-sm text-gray-600">
                    Website: <a href="https://www.befrienders.org/" target="_blank" rel="noopener noreferrer" 
                    className="text-blue-600 hover:underline">https://www.befrienders.org/</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Crisis Text Line */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Mail className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Crisis Text Line (US)</h3>
                  <p className="text-lg font-mono text-blue-600 mb-2">
                    Text HOME to 741741
                  </p>
                  <p className="text-gray-700">
                    Free, 24/7 support for those in crisis
                  </p>
                </div>
              </div>
            </div>

            {/* National Suicide Prevention Lifeline (US) */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">National Suicide Prevention Lifeline (US)</h3>
                  <a href="tel:988" className="text-lg font-mono text-blue-600 hover:underline">
                    988
                  </a>
                  <p className="text-gray-700 mt-1">
                    24/7, free and confidential support
                  </p>
                </div>
              </div>
            </div>

            {/* Lifeline Australia */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Lifeline Australia</h3>
                  <a href="tel:131114" className="text-lg font-mono text-blue-600 hover:underline">
                    13 11 14
                  </a>
                  <p className="text-gray-700 mt-1">
                    24-hour crisis support and suicide prevention
                  </p>
                </div>
              </div>
            </div>

            {/* Kids Help Phone (Canada) */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <Phone className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Kids Help Phone (Canada)</h3>
                  <a href="tel:18006686868" className="text-lg font-mono text-blue-600 hover:underline">
                    1-800-668-6868
                  </a>
                  <p className="text-gray-700 mt-1">
                    24/7 support for young people
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Support Section */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-4 text-center">
            Remember: You Are Not Alone
          </h2>
          <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto">
            Reaching out for help is a sign of strength, not weakness. These services are confidential, 
            non-judgmental, and available when you need them most. Your life has value, and there are 
            people who want to help you through difficult times.
          </p>
          <div className="text-center mt-6">
            <Heart className="w-12 h-12 text-red-500 mx-auto" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeepingYouSafePage;